<template>
  <div class="projects-view">
    <div class="page-header">
      <h1 class="page-title">Projects</h1>
      <button class="btn btn-primary" @click="openForm(null)">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        New Project
      </button>
    </div>

    <div v-if="loading" class="spinner"></div>

    <div v-else-if="projects.length === 0" class="empty-state">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity=".3"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
      <p>No projects yet.</p>
    </div>

    <div v-else class="projects-grid">
      <div v-for="p in projects" :key="p.id" class="project-card">
        <div class="project-color" :style="{ background: p.color }"></div>
        <div class="project-body">
          <div class="project-top">
            <h3 class="project-name">{{ p.name }}</h3>
            <span class="vis-badge" :class="p.visibility === 'private' ? 'vis-private' : 'vis-shared'">
              {{ p.visibility === 'private' ? 'Private' : 'Shared' }}
            </span>
          </div>
          <p v-if="p.description" class="project-desc">{{ p.description }}</p>
          <div class="project-footer">
            <span class="task-count">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
              {{ p.task_count }} open {{ p.task_count === 1 ? 'task' : 'tasks' }}
            </span>
            <div class="project-actions">
              <router-link :to="'/tasks?project_id=' + p.id" class="btn btn-ghost btn-sm">View tasks</router-link>
              <button class="btn btn-ghost btn-sm btn-icon" @click="openForm(p)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </button>
              <button class="btn btn-ghost btn-sm btn-icon danger-icon" title="Archive project" @click="archiveProject(p)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Project form modal -->
    <div v-if="showForm" class="modal-backdrop" @click.self="showForm = false">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ editing ? 'Edit Project' : 'New Project' }}</h2>
          <button class="btn btn-icon modal-close" @click="showForm = false">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <form @submit.prevent="submitProject">
          <div class="form-group">
            <label>Name *</label>
            <input v-model="pForm.name" class="form-control" required maxlength="100" />
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea v-model="pForm.description" class="form-control" rows="2"></textarea>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Color</label>
              <div class="color-picker">
                <input type="color" v-model="pForm.color" class="color-input" />
                <span class="color-preview" :style="{ background: pForm.color }"></span>
                <input v-model="pForm.color" class="form-control" style="font-size:12px" placeholder="#6366f1" />
              </div>
            </div>
            <div class="form-group">
              <label>Visibility</label>
              <select v-model="pForm.visibility" class="form-control">
                <option value="shared">Shared (team sees this)</option>
                <option value="private">Private (only you)</option>
              </select>
            </div>
          </div>
          <p v-if="pError" class="form-error">{{ pError }}</p>
          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" @click="showForm = false">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="pSaving">
              {{ pSaving ? 'Saving…' : (editing ? 'Save' : 'Create') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import api from '@/api'

export default {
  name: 'ProjectsView',
  setup() {
    const projects = ref([])
    const loading  = ref(true)
    const showForm = ref(false)
    const editing  = ref(null)
    const pSaving  = ref(false)
    const pError   = ref('')

    const pForm = reactive({ name: '', description: '', color: '#6366f1', visibility: 'shared' })

    async function load() {
      loading.value = true
      try { projects.value = (await api.getProjects()).data || [] } catch {} finally { loading.value = false }
    }

    function openForm(proj) {
      editing.value = proj
      Object.assign(pForm, {
        name:        proj?.name        || '',
        description: proj?.description || '',
        color:       proj?.color       || '#6366f1',
        visibility:  proj?.visibility  || 'shared',
      })
      pError.value  = ''
      showForm.value = true
    }

    async function submitProject() {
      pError.value  = ''
      pSaving.value = true
      try {
        const data = { name: pForm.name, description: pForm.description || null, color: pForm.color, visibility: pForm.visibility }
        if (editing.value) {
          await api.updateProject(editing.value.id, data)
        } else {
          await api.createProject(data)
        }
        showForm.value = false
        await load()
      } catch (e) {
        pError.value = e.message || 'Save failed'
      } finally {
        pSaving.value = false
      }
    }

    async function archiveProject(proj) {
      if (!confirm(`Archive project "${proj.name}"? It will no longer appear in the project list.`)) return
      try { await api.archiveProject(proj.id); projects.value = projects.value.filter(p => p.id !== proj.id) } catch {}
    }

    onMounted(load)
    return { projects, loading, showForm, editing, pForm, pSaving, pError, openForm, submitProject, archiveProject }
  },
}
</script>

<style scoped>
.projects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}
.project-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  display: flex;
  transition: border-color .15s;
}
.project-card:hover { border-color: var(--primary); }
.project-color { width: 5px; flex-shrink: 0; }
.project-body  { flex: 1; padding: 16px; }

.project-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.project-name { font-size: 15px; font-weight: 600; }
.vis-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
}
.vis-shared  { background: rgba(34,197,94,.1);  color: var(--success); border: 1px solid rgba(34,197,94,.3); }
.vis-private { background: rgba(245,158,11,.1); color: var(--warning); border: 1px solid rgba(245,158,11,.3); }

.project-desc { font-size: 12px; color: var(--muted); margin-bottom: 14px; }
.project-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.task-count { display: flex; align-items: center; gap: 5px; font-size: 12px; color: var(--muted); }
.project-actions { display: flex; gap: 4px; }
.danger-icon:hover { color: var(--danger); background: rgba(239,68,68,.1) !important; }

.modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.6);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 20px;
}
.modal {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  width: 100%; max-width: 480px;
  padding: 28px;
  box-shadow: var(--shadow);
}
.modal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.modal-header h2 { font-size: 18px; font-weight: 700; }
.modal-close { color: var(--muted); }

.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

.color-picker { display: flex; align-items: center; gap: 8px; }
.color-input  { width: 36px; height: 36px; border: none; background: none; cursor: pointer; padding: 0; }
.color-preview { width: 20px; height: 20px; border-radius: 50%; flex-shrink: 0; }

.form-error {
  color: var(--danger); font-size: 13px; margin-bottom: 12px;
  padding: 8px 12px;
  background: rgba(239,68,68,.1);
  border-radius: 6px;
}
.modal-actions {
  display: flex; justify-content: flex-end; gap: 10px;
  margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--border);
}
</style>
